
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const PropositoSection = () => {
  const [stage, setStage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timers = [
            setTimeout(() => setStage(1), 100),
            setTimeout(() => setStage(2), 350),
            setTimeout(() => setStage(3), 650),
            setTimeout(() => setStage(4), 950),
            setTimeout(() => setStage(5), 1250),
          ];
          observer.disconnect();
          return () => timers.forEach(clearTimeout);
        }
      },
      { threshold: 0.2 }
    );

    const el = sectionRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 overflow-hidden"
    >
      {/* Background image + overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9b437358c1a891823f01a9f2432be257.jpg"
          alt="Nascer do sol sobre o oceano"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-luxury-black via-luxury-black/85 to-luxury-black/90" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Header line */}
          <div
            className="flex items-center justify-center gap-4 mb-8 transition-all duration-700"
            style={{
              opacity: stage >= 1 ? 1 : 0,
              transform: stage >= 1 ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            <div
              className="h-px bg-gradient-to-r from-transparent to-gold-500/50 transition-all duration-700"
              style={{ width: stage >= 1 ? '4rem' : '0rem', transitionDelay: '200ms' }}
            />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-500/60">
              Meu Propósito
            </span>
            <div
              className="h-px bg-gradient-to-l from-transparent to-gold-500/50 transition-all duration-700"
              style={{ width: stage >= 1 ? '4rem' : '0rem', transitionDelay: '200ms' }}
            />
          </div>

          {/* Title */}
          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-8 transition-all duration-800"
            style={{
              opacity: stage >= 2 ? 1 : 0,
              transform: stage >= 2 ? 'translateY(0)' : 'translateY(30px)',
              filter: stage >= 2 ? 'blur(0px)' : 'blur(4px)',
            }}
          >
            Ajudar o maior número possível de pessoas para que elas{' '}
            <span className="italic text-gold-400">
              não precisem levar 30 anos
            </span>{' '}
            para serem livres.
          </h2>

          {/* Divider */}
          <div
            className="mx-auto my-10 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent transition-all duration-700"
            style={{
              width: stage >= 3 ? '6rem' : '0rem',
              opacity: stage >= 3 ? 1 : 0,
            }}
          />

          {/* Paragraph */}
          <p
            className="font-['Montserrat'] text-base text-white/45 leading-relaxed font-light tracking-wide max-w-2xl mx-auto mb-12 transition-all duration-700"
            style={{
              opacity: stage >= 4 ? 1 : 0,
              transform: stage >= 4 ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            O Carta do Eu Futuro nasceu da minha dor e se transformou no meu
            propósito. Cada ferramenta, cada técnica, cada palavra foi pensada
            para que você não precise passar pelo que eu passei. Para que a sua
            libertação comece agora — não daqui a 30 anos.
          </p>

          {/* Buttons */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-5 transition-all duration-700"
            style={{
              opacity: stage >= 5 ? 1 : 0,
              transform: stage >= 5 ? 'translateY(0) scale(1)' : 'translateY(15px) scale(0.95)',
            }}
          >
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              className="group bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-sm px-10 py-4 rounded-lg hover:shadow-[0_0_30px_rgba(201,168,76,0.3)] transition-all duration-500 hover:scale-105 flex items-center gap-3 whitespace-nowrap cursor-pointer tracking-wider uppercase"
            >
              Comece sua transformação
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-2 transition-transform duration-300" />
            </a>

            <a
              href="/comunidade"
              onClick={(e) => {
                e.preventDefault();
                navigate('/comunidade');
              }}
              className="font-['Montserrat'] text-sm text-gold-400 border border-gold-500/30 px-8 py-4 rounded-lg hover:bg-gold-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wider uppercase"
            >
              Conheça a comunidade
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropositoSection;
